import { normalizeMeasurementPreference } from '../domain/units'
import {
  deleteDB,
  type DBSchema,
  type IDBPDatabase,
  type IDBPTransaction,
  openDB,
  unwrap,
} from 'idb'
import { isPageKind } from '../domain/urlCategory'
import {
  classifyContent,
  compareNoteVersions,
  deriveTitle,
  isGoogleKeepRollbackTombstone,
  repairEscapedUrls,
  type ActiveDraft,
  type Note,
  type NoteRevision,
  NOTE_SYNC_SCHEMA_VERSION,
  type SyncOperation,
  type UrlEntity,
} from '../domain/note'
import { normalizeReminder, normalizeReminderQuickOptions } from '../domain/reminder'
import { createId } from '../utils/id'
import {
  compareSettingsVersions,
  isAppStartMode,
  isUiSize,
  isWeekStartsOn,
  normalizeWeekStartsOn,
  settingEntityType,
  updateDeviceSettings,
  updateReminderQuickOptions,
  updateWeekStartsOn,
  type DeviceSettings,
  type GlobalSettings,
  type SettingsRecord,
} from '../domain/settings'
import { APP_SETTINGS } from '../config/appSettings'
import { activeWorkspace, assertWorkspaceActive, onWorkspaceRetired, readRetiringDraft, retireWorkspace, selectedDatabaseName } from './workspace'
import {
  APP_DATABASE_NAME,
  DEVICE_STORAGE_KEY,
  previousBrandStorageName,
} from '../utils/storageNames'
import {
  assetPreviewBlob,
  type NoteAsset,
  type RemoteNoteAsset,
} from '../assets/assetTypes'
import type { LinkMetadata } from '../links/linkMetadata'
import {
  buildLocalDataContinuitySnapshot,
  type LocalDataContinuitySnapshot,
} from './continuity'
import type { LocalRecoveryExportSource } from './localRecoveryExport'
import {
  RecoveryDraftConflictError,
  type LocalRecoveryImportPlan,
  type LocalRecoveryImportResult,
} from './localRecoveryImport'
import { shouldPersistActiveDraft } from '../sync/activeNoteSync'
import { shareOriginalMetaRecord } from '../links/shareOriginalRecord'

const STORAGE_BRAND_MIGRATION_KEY = 'storage-brand-migration:v1'
const DEVICE_ID_META_KEY = 'device-id:v1'
const DEVICE_ID_COOKIE_NAME = 'minimany_device_id_v1'
const DEVICE_ID_COOKIE_MAX_AGE_SECONDS = 10 * 365 * 24 * 60 * 60
const DATABASE_STORE_NAMES = [
  'notes',
  'revisions',
  'outbox',
  'settings',
  'drafts',
  'meta',
  'assets',
  'linkPreviews',
] as const

interface MetaRecord {
  key: string
  value: string | number | boolean
}

interface LinkPreviewRecord extends LinkMetadata {
  canonicalUrl: string
  fetchedAt: number
  metadataPolicyVersion: number | null
  metadataFetchAttemptCount?: number
  metadataNextRetryAt?: number | null
}

type HydratedRemoteNote = Note & { linkPreviews?: unknown }

export interface CachedLinkMetadata {
  metadata: LinkMetadata
  fetchedAt: number
  metadataPolicyVersion: number | null
}

interface MiniManySchema extends DBSchema {
  notes: {
    key: string
    value: Note
    indexes: {
      'by-modified': number
      'by-modified-id': [number, string]
      'by-status': string
      'by-expiry': number
    }
  }
  revisions: {
    key: string
    value: NoteRevision
    indexes: {
      'by-note': string
      'by-modified': number
    }
  }
  outbox: {
    key: string
    value: SyncOperation
    indexes: {
      'by-next-attempt': number
      'by-entity': [string, string]
    }
  }
  settings: {
    key: string
    value: SettingsRecord
  }
  drafts: {
    key: string
    value: ActiveDraft
  }
  meta: {
    key: string
    value: MetaRecord
  }
  assets: {
    key: string
    value: NoteAsset
    indexes: {
      'by-note': string
      'by-state': string
      'by-next-attempt': number
    }
  }
  linkPreviews: {
    key: string
    value: LinkPreviewRecord
  }
}

let databasePromise: Promise<IDBPDatabase<MiniManySchema>> | null = null
const APPLICATION_DATABASE_VERSION = 8

function normalizeStoredNote(note: Note): Note {
  const assetIds = Array.isArray(note.assetIds) ? note.assetIds : []
  const urls = repairEscapedUrls(note.urls).map((url) => (
    typeof url.metadataPolicyVersion === 'number'
      && Number.isInteger(url.metadataFetchAttemptCount)
      && url.metadataFetchAttemptCount >= 0
      && (url.metadataNextRetryAt === null || (
        typeof url.metadataNextRetryAt === 'number' && Number.isFinite(url.metadataNextRetryAt)
      ))
      ? url
      : {
          ...url,
          metadataPolicyVersion: typeof url.metadataPolicyVersion === 'number'
            ? url.metadataPolicyVersion
            : null,
          metadataFetchAttemptCount: Number.isInteger(url.metadataFetchAttemptCount)
            && url.metadataFetchAttemptCount >= 0
            ? url.metadataFetchAttemptCount
            : 0,
          metadataNextRetryAt: typeof url.metadataNextRetryAt === 'number'
            && Number.isFinite(url.metadataNextRetryAt)
            ? url.metadataNextRetryAt
            : null,
        }
  ))
  const derivedTitle = deriveTitle(note.body, urls, assetIds.length > 0)
  const reminder = normalizeReminder(note.reminder)
  const quickAccess = note.quickAccess === true
  const quickAccessAt = quickAccess && typeof note.quickAccessAt === 'number'
    && Number.isFinite(note.quickAccessAt)
    ? note.quickAccessAt
    : null
  if (
    assetIds === note.assetIds
    && urls.every((url, index) => url === note.urls[index])
    && derivedTitle === note.derivedTitle
    && reminder === note.reminder
    && quickAccess === note.quickAccess
    && quickAccessAt === note.quickAccessAt
  ) return note
  return { ...note, assetIds, urls, derivedTitle, reminder, quickAccess, quickAccessAt }
}

function previewRecord(url: UrlEntity): LinkPreviewRecord | null {
  if (!url.canonicalUrl || url.fetchedAt === null) return null
  return {
    canonicalUrl: url.canonicalUrl,
    title: url.title,
    description: url.description,
    imageUrl: url.imageUrl,
    durationSeconds: url.durationSeconds,
    siteName: url.siteName,
    author: url.author,
    publishedAt: url.publishedAt,
    ...(isPageKind(url.pageKind) ? { pageKind: url.pageKind } : {}),
    fetchedAt: url.fetchedAt,
    metadataPolicyVersion: url.metadataPolicyVersion,
    metadataFetchAttemptCount: url.metadataFetchAttemptCount,
    metadataNextRetryAt: url.metadataNextRetryAt,
  }
}

async function putPreviewRecords(
  store: { put(value: LinkPreviewRecord): Promise<IDBValidKey> },
  urls: UrlEntity[],
): Promise<void> {
  await Promise.all(urls.map((url) => {
    const record = previewRecord(url)
    return record ? store.put(record) : Promise.resolve(undefined)
  }))
}

function hydrateUrlsFromCachedPreviews(
  urls: UrlEntity[],
  previews: Array<LinkPreviewRecord | undefined>,
): UrlEntity[] {
  const previewsByCanonicalUrl = new Map<string, LinkPreviewRecord>()
  for (const preview of previews) {
    if (!preview) continue
    const current = previewsByCanonicalUrl.get(preview.canonicalUrl)
    const previewPolicyVersion = preview.metadataPolicyVersion ?? 0
    const currentPolicyVersion = current?.metadataPolicyVersion ?? 0
    if (
      !current
      || previewPolicyVersion > currentPolicyVersion
      || (previewPolicyVersion === currentPolicyVersion && preview.fetchedAt >= current.fetchedAt)
    ) {
      previewsByCanonicalUrl.set(preview.canonicalUrl, preview)
    }
  }
  return urls.map((url) => {
    const preview = url.canonicalUrl ? previewsByCanonicalUrl.get(url.canonicalUrl) : undefined
    if (!preview) return url
    return {
      ...url,
      title: preview.title,
      description: preview.description,
      imageUrl: preview.imageUrl,
      durationSeconds: preview.durationSeconds,
      siteName: preview.siteName,
      author: preview.author,
      publishedAt: preview.publishedAt,
      pageKind: preview.pageKind ?? url.pageKind ?? null,
      fetchedAt: preview.fetchedAt,
      metadataPolicyVersion: preview.metadataPolicyVersion,
      metadataFetchAttemptCount: preview.metadataFetchAttemptCount ?? 0,
      metadataNextRetryAt: preview.metadataNextRetryAt ?? null,
    }
  })
}

function isNullableText(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value))
}

function hydratedRemotePreview(value: unknown): LinkPreviewRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const preview = value as Record<string, unknown>
  const metadata = preview.metadata
  const metadataPolicyVersion = preview.metadataPolicyVersion
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null
  const fields = metadata as Record<string, unknown>
  if (
    typeof preview.canonicalUrl !== 'string'
    || preview.canonicalUrl.length === 0
    || typeof preview.fetchedAt !== 'number'
    || !Number.isFinite(preview.fetchedAt)
    || typeof metadataPolicyVersion !== 'number'
    || !Number.isInteger(metadataPolicyVersion)
    || metadataPolicyVersion < 1
    || !isNullableText(fields.title)
    || !isNullableText(fields.description)
    || !isNullableText(fields.imageUrl)
    || !isNullableNumber(fields.durationSeconds)
    || !isNullableText(fields.siteName)
    || !isNullableText(fields.author)
    || !isNullableNumber(fields.publishedAt)
  ) return null
  return {
    canonicalUrl: preview.canonicalUrl,
    fetchedAt: preview.fetchedAt,
    metadataPolicyVersion,
    title: fields.title,
    description: fields.description,
    imageUrl: fields.imageUrl,
    durationSeconds: fields.durationSeconds,
    siteName: fields.siteName,
    author: fields.author,
    publishedAt: fields.publishedAt,
    ...(isPageKind(fields.pageKind) ? { pageKind: fields.pageKind } : {}),
    metadataFetchAttemptCount: 0,
    metadataNextRetryAt: null,
  }
}

export interface NotePageCursor {
  modifiedAt: number
  id: string
}

export interface NotePage {
  notes: Note[]
  nextCursor: NotePageCursor | null
}

export interface NativeCaptureDraftInput {
  id: string
  revision: number
  text: string
  createdAt: number
  updatedAt: number
}

export interface NativeCaptureDraftAdoption {
  draft: ActiveDraft
  alreadyAdopted: boolean
}

export interface NativeShareDraftInput {
  id: string
  revision: number
  body: string
  /** The share before its packaging was trimmed, when trimming changed it. */
  shareOriginal?: string
  assets: NoteAsset[]
  createdAt: number
  updatedAt: number
}

export interface NativeShareDraftAdoption {
  draft: ActiveDraft
  assets: NoteAsset[]
  alreadyAdopted: boolean
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  })
}

function openPreviousDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(previousBrandStorageName())
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function migratePreviousBrandDatabase(
  database: IDBPDatabase<MiniManySchema>,
): Promise<void> {
  console.info('miniMany storage: checking migration marker')
  if ((await database.get('meta', STORAGE_BRAND_MIGRATION_KEY))?.value === true) {
    console.info('miniMany storage: migration already complete')
    void deleteDB(previousBrandStorageName()).catch(() => undefined)
    return
  }

  console.info('miniMany storage: opening previous namespace')
  const previousDatabase = await openPreviousDatabase()
  console.info('miniMany storage: previous namespace open')
  const targetDatabase = unwrap(database)
  try {
    for (const storeName of DATABASE_STORE_NAMES) {
      if (!previousDatabase.objectStoreNames.contains(storeName)) continue
      const values = await requestResult(
        previousDatabase.transaction(storeName, 'readonly').objectStore(storeName).getAll(),
      )
      if (values.length === 0) continue
      const transaction = targetDatabase.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      for (const value of values) store.put(value)
      await transactionDone(transaction)
    }
  } finally {
    previousDatabase.close()
  }

  await database.put('meta', { key: STORAGE_BRAND_MIGRATION_KEY, value: true })
  void deleteDB(previousBrandStorageName()).catch(() => undefined)
}

async function openApplicationDatabase(): Promise<IDBPDatabase<MiniManySchema>> {
  const databaseName = selectedDatabaseName()
  console.info('miniMany storage: opening application database')
  try {
    return await openDB<MiniManySchema>(databaseName, APPLICATION_DATABASE_VERSION, {
      blocking() {
        if (activeWorkspace()) retireWorkspace()
        else void databasePromise?.then(db => db.close())
      },
      blocked(currentVersion, blockedVersion) {
        console.warn('miniMany storage: database upgrade blocked by another connection', { currentVersion, blockedVersion })
      },
      upgrade(database, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
          const notes = database.createObjectStore('notes', { keyPath: 'id' })
          notes.createIndex('by-modified', 'modifiedAt')
          notes.createIndex('by-modified-id', ['modifiedAt', 'id'])
          notes.createIndex('by-status', 'status')
          notes.createIndex('by-expiry', 'expiresAt')

          const revisions = database.createObjectStore('revisions', { keyPath: 'id' })
          revisions.createIndex('by-note', 'noteId')
          revisions.createIndex('by-modified', 'modifiedAt')

          const outbox = database.createObjectStore('outbox', { keyPath: 'id' })
          outbox.createIndex('by-next-attempt', 'nextAttemptAt')

          database.createObjectStore('drafts', { keyPath: 'key' })
          database.createObjectStore('meta', { keyPath: 'key' })
        }

        if (oldVersion >= 1 && oldVersion < 2) {
          const notes = transaction.objectStore('notes')
          if (!notes.indexNames.contains('by-modified-id')) {
            notes.createIndex('by-modified-id', ['modifiedAt', 'id'])
          }
        }

        if (oldVersion < 3) {
          database.createObjectStore('settings', { keyPath: 'key' })
          const outbox = transaction.objectStore('outbox')
          if (!outbox.indexNames.contains('by-entity')) {
            outbox.createIndex('by-entity', ['entityType', 'entityId'])
          }
        }

        if (oldVersion < 4) {
          const assets = database.createObjectStore('assets', { keyPath: 'id' })
          assets.createIndex('by-note', 'noteId')
          assets.createIndex('by-state', 'state')
          assets.createIndex('by-next-attempt', 'nextAttemptAt')
        }

        if (oldVersion < 5) {
          database.createObjectStore('linkPreviews', { keyPath: 'canonicalUrl' })
        }

        // v8 retires only the obsolete sidecar stores. Never lower the DB version.
        if (oldVersion < 8) {
          const rawDatabase = unwrap(database)
          for (const name of [
            'noteSemantics', 'semanticConstraints', 'intelligencePreferences',
            'semanticAuthorContexts', 'intelligenceOutbox',
          ]) {
            if (rawDatabase.objectStoreNames.contains(name)) rawDatabase.deleteObjectStore(name)
          }
        }
      },
    })
  } catch (error) {
    if (!(error instanceof DOMException) || error.name !== 'VersionError') throw error

    // Development APKs can move between worktrees while retaining the same
    // https://localhost sandbox. A worktree that owns an older schema must not
    // request a downgrade: IndexedDB rejects it before any application store can
    // be read. Reopen the already-upgraded database at its current version and
    // leave stores owned by the newer client untouched.
    return openDB<MiniManySchema>(databaseName)
  }
}

export function getDatabase(): Promise<IDBPDatabase<MiniManySchema>> {
  try { assertWorkspaceActive() } catch (error) { return Promise.reject(error) }
  databasePromise ??= openApplicationDatabase().then(async (database) => {
    console.info('miniMany storage: application database open', { version: database.version })
    // Only the unscoped migration/test lane may run the old destructive importer.
    // Account workspaces must never inherit a previous namespace implicitly.
    if (!activeWorkspace()) await migratePreviousBrandDatabase(database)
    if (activeWorkspace()?.owner) {
      const workspace = activeWorkspace()!
      const tx = database.transaction('meta', 'readwrite')
      const owner = (await tx.store.get('sync-owner'))?.value
      if (owner && owner !== workspace.owner) { tx.abort(); await tx.done.catch(() => undefined); database.close(); throw new Error('Offline account mismatch.') }
      await tx.store.put({ key: 'sync-owner', value: workspace.owner! })
      await tx.store.put({ key: 'sync-environment', value: workspace.scope.environment })
      await tx.done
    }
    onWorkspaceRetired(async () => {
      try {
        const draft = readRetiringDraft()
        if (draft) await database.put('drafts', draft)
      } finally { database.close() }
    })
    try { assertWorkspaceActive() } catch (error) { database.close(); throw error }
    console.info('miniMany storage: application database ready')
    return database
  }).catch((error) => {
    databasePromise = null
    throw error
  })
  return databasePromise.then(database => { assertWorkspaceActive(); return database })
}

export class NoteRepository {
  async adoptNativeCaptureDraft(
    input: NativeCaptureDraftInput,
  ): Promise<NativeCaptureDraftAdoption> {
    const database = await getDatabase()
    const transaction = database.transaction(['drafts', 'notes', 'meta'], 'readwrite')
    const drafts = transaction.objectStore('drafts')
    const notes = transaction.objectStore('notes')
    const meta = transaction.objectStore('meta')
    const adoptionKey = `native-intent:v1:${input.id}`
    const [adoption, activeDraft, existingNote] = await Promise.all([
      meta.get(adoptionKey),
      drafts.get('active'),
      notes.get(input.id),
    ])
    const adoptedRevision = typeof adoption?.value === 'number' ? adoption.value : 0
    const alreadyAdopted = adoptedRevision >= input.revision
    const draft = activeDraft?.noteId === input.id
      ? {
          ...activeDraft,
          body: alreadyAdopted ? activeDraft.body : input.text,
          updatedAt: alreadyAdopted ? activeDraft.updatedAt : input.updatedAt,
        }
      : existingNote && alreadyAdopted
        ? {
            key: 'active' as const,
            noteId: existingNote.id,
            body: existingNote.body,
            annotation: existingNote.annotation,
            userTags: [...existingNote.userTags],
            assetIds: [...(existingNote.assetIds ?? [])],
            expiresAt: existingNote.expiresAt,
            createdAt: existingNote.createdAt,
            updatedAt: input.updatedAt,
          }
        : {
            key: 'active' as const,
            noteId: input.id,
            body: input.text,
            annotation: '',
            userTags: [],
            assetIds: [],
            expiresAt: null,
            createdAt: input.createdAt,
            updatedAt: input.updatedAt,
          }

    await drafts.put(draft)
    if (!alreadyAdopted) await meta.put({ key: adoptionKey, value: input.revision })
    await transaction.done
    return { draft, alreadyAdopted }
  }

  async adoptNativeShareDraft(
    input: NativeShareDraftInput,
  ): Promise<NativeShareDraftAdoption> {
    const assetIds = input.assets.map((asset) => asset.id)
    if (
      new Set(assetIds).size !== assetIds.length
      || input.assets.some((asset) => asset.noteId !== input.id || asset.kind !== 'image')
    ) {
      throw new Error('Native Share assets do not match their draft.')
    }
    const database = await getDatabase()
    const transaction = database.transaction(['drafts', 'notes', 'assets', 'meta'], 'readwrite')
    const drafts = transaction.objectStore('drafts')
    const notes = transaction.objectStore('notes')
    const assets = transaction.objectStore('assets')
    const meta = transaction.objectStore('meta')
    const adoptionKey = `native-intent:v1:${input.id}`
    const [adoption, activeDraft, existingNote] = await Promise.all([
      meta.get(adoptionKey),
      drafts.get('active'),
      notes.get(input.id),
    ])
    const adoptedRevision = typeof adoption?.value === 'number' ? adoption.value : 0
    const alreadyAdopted = adoptedRevision >= input.revision
    const draft = activeDraft?.noteId === input.id
      ? alreadyAdopted
        ? activeDraft
        : {
            ...activeDraft,
            body: input.body,
            assetIds,
            updatedAt: input.updatedAt,
          }
      : existingNote && alreadyAdopted
        ? {
            key: 'active' as const,
            noteId: existingNote.id,
            body: existingNote.body,
            annotation: existingNote.annotation,
            userTags: [...existingNote.userTags],
            assetIds: [...(existingNote.assetIds ?? [])],
            expiresAt: existingNote.expiresAt,
            createdAt: existingNote.createdAt,
            updatedAt: input.updatedAt,
          }
        : {
            key: 'active' as const,
            noteId: input.id,
            body: input.body,
            annotation: '',
            userTags: [],
            assetIds,
            expiresAt: null,
            createdAt: input.createdAt,
            updatedAt: input.updatedAt,
          }

    if (!alreadyAdopted) {
      await Promise.all([
        ...input.assets.map((asset) => assets.put(asset)),
        drafts.put(draft),
        meta.put({ key: adoptionKey, value: input.revision }),
        ...(input.shareOriginal
          ? [meta.put(shareOriginalMetaRecord(input.id, { tidy: input.body, original: input.shareOriginal, mode: 'tidy' }))]
          : []),
      ])
    } else if (activeDraft?.noteId !== input.id) {
      await drafts.put(draft)
    }
    const adoptedAssets = alreadyAdopted
      ? (await Promise.all(draft.assetIds.map((id) => assets.get(id))))
          .filter((asset): asset is NoteAsset => asset !== undefined)
      : input.assets
    await transaction.done
    return { draft, assets: adoptedAssets, alreadyAdopted }
  }

  async importLocalRecovery(
    plan: LocalRecoveryImportPlan,
  ): Promise<LocalRecoveryImportResult> {
    const database = await getDatabase()
    const transaction = database.transaction(
      [
        'notes',
        'drafts',
        'revisions',
        'outbox',
        'settings',
        'assets',
      ],
      'readwrite',
    )
    const notes = transaction.objectStore('notes')
    const drafts = transaction.objectStore('drafts')
    const revisions = transaction.objectStore('revisions')
    const outbox = transaction.objectStore('outbox')
    const settings = transaction.objectStore('settings')
    const assets = transaction.objectStore('assets')
    const existingDraft = await drafts.get('active')
    if (plan.activeDraft && existingDraft) {
      const existingNote = await notes.get(existingDraft.noteId)
      const sameDraft = existingDraft.noteId === plan.activeDraft.noteId
        && existingDraft.body === plan.activeDraft.body
        && existingDraft.annotation === plan.activeDraft.annotation
        && existingDraft.expiresAt === plan.activeDraft.expiresAt
        && existingDraft.userTags.join('\u0000') === plan.activeDraft.userTags.join('\u0000')
        && existingDraft.assetIds.join('\u0000') === plan.activeDraft.assetIds.join('\u0000')
      if (!sameDraft && shouldPersistActiveDraft(existingDraft, existingNote)) {
        throw new RecoveryDraftConflictError()
      }
    }

    const revisionById = new Map(plan.noteRevisions.map((revision) => [revision.id, revision]))
    let importedNotes = 0
    for (const imported of plan.notes) {
      const current = await notes.get(imported.id)
      if (current && compareNoteVersions(current, imported) >= 0) continue
      const note = { ...imported, localSyncState: 'pending' as const }
      const importedRevision = revisionById.get(note.revisionId)
      const revision: NoteRevision = importedRevision
        ? { ...importedRevision, snapshot: note }
        : {
            id: note.revisionId,
            noteId: note.id,
            snapshot: note,
            modifiedAt: note.modifiedAt,
            deviceId: note.lastModifiedDeviceId,
            reason: 'restore',
            clientSchemaVersion: NOTE_SYNC_SCHEMA_VERSION,
          }
      const previousOperationIds = await outbox.index('by-entity').getAllKeys(['note', note.id])
      await Promise.all([
        notes.put(note),
        revisions.put(revision),
        ...previousOperationIds.map((operationId) => outbox.delete(operationId)),
        outbox.put({
          id: note.revisionId,
          entityType: 'note',
          entityId: note.id,
          revisionId: note.revisionId,
          operation: note.status === 'trash' ? 'delete' : 'upsert',
          createdAt: note.modifiedAt,
          attemptCount: 0,
          nextAttemptAt: note.modifiedAt,
          lastErrorCode: null,
        }),
      ])
      importedNotes += 1
    }

    let importedGlobalSettings = false
    if (plan.globalSettings) {
      const current = await settings.get('global') as GlobalSettings | undefined
      if (!current || compareSettingsVersions(current, plan.globalSettings) < 0) {
        const globalSettings = { ...plan.globalSettings, localSyncState: 'pending' as const }
        const previousOperationIds = await outbox.index('by-entity').getAllKeys([
          'global-settings',
          'global',
        ])
        await Promise.all([
          settings.put(globalSettings),
          ...previousOperationIds.map((operationId) => outbox.delete(operationId)),
          outbox.put({
            id: globalSettings.revisionId,
            entityType: 'global-settings',
            entityId: 'global',
            revisionId: globalSettings.revisionId,
            operation: 'upsert',
            createdAt: globalSettings.modifiedAt,
            attemptCount: 0,
            nextAttemptAt: globalSettings.modifiedAt,
            lastErrorCode: null,
          }),
        ])
        importedGlobalSettings = true
      }
    }

    let importedAssets = 0
    for (const imported of plan.assets) {
      const current = await assets.get(imported.id)
      if (current && current.modifiedAt >= imported.modifiedAt) continue
      await assets.put(imported)
      importedAssets += 1
    }

    const importedDraft = Boolean(plan.activeDraft && (
      !existingDraft
      || existingDraft.noteId !== plan.activeDraft.noteId
      || existingDraft.updatedAt !== plan.activeDraft.updatedAt
    ))
    if (plan.activeDraft) await drafts.put(plan.activeDraft)
    await transaction.done
    return { importedNotes, importedAssets, importedGlobalSettings, importedDraft }
  }

  async readLocalRecoveryExportSource(): Promise<LocalRecoveryExportSource> {
    const database = await getDatabase()
    const transaction = database.transaction(
      [
        'notes',
        'drafts',
        'revisions',
        'outbox',
        'settings',
        'assets',
      ],
      'readonly',
    )
    const [
      notes,
      activeDraft,
      revisions,
      outbox,
      settings,
      assets,
    ] = await Promise.all([
      transaction.objectStore('notes').getAll(),
      transaction.objectStore('drafts').get('active'),
      transaction.objectStore('revisions').getAll(),
      transaction.objectStore('outbox').getAll(),
      transaction.objectStore('settings').getAll(),
      transaction.objectStore('assets').getAll(),
    ])
    await transaction.done
    return {
      notes: notes.map(normalizeStoredNote),
      activeDraft,
      revisions,
      outbox,
      settings,
      assets,
    }
  }

  async inspectLocalDataContinuity(now = Date.now()): Promise<LocalDataContinuitySnapshot> {
    const database = await getDatabase()
    const transaction = database.transaction(
      ['notes', 'drafts', 'outbox', 'settings', 'assets', 'meta'],
      'readonly',
    )
    const [notes, activeDraft, outbox, settings, assets, metadata] = await Promise.all([
      transaction.objectStore('notes').getAll(),
      transaction.objectStore('drafts').get('active'),
      transaction.objectStore('outbox').getAll(),
      transaction.objectStore('settings').getAll(),
      transaction.objectStore('assets').getAll(),
      transaction.objectStore('meta').getAll(),
    ])
    await transaction.done
    const meta = new Map(metadata.map((entry) => [entry.key, entry.value]))
    return buildLocalDataContinuitySnapshot({
      notes: notes.map(normalizeStoredNote),
      activeDraft,
      outbox,
      settings,
      assets,
      accountBound: typeof meta.get('sync-owner') === 'string',
      environmentBound: typeof meta.get('sync-environment') === 'string',
      noteCursorPresent: [...meta.keys()].some((key) => key.startsWith('sync-cursor:')),
      assetCursorPresent: typeof meta.get('asset-sync-cursor') === 'string',
      now,
    })
  }

  private async replaceSettingsOutboxOperation(
    transaction: IDBPTransaction<MiniManySchema, ('settings' | 'outbox')[], 'readwrite'>,
    settings: SettingsRecord,
  ): Promise<void> {
    const outbox = transaction.objectStore('outbox')
    const entityType = settingEntityType(settings)
    const oldOperationIds = await outbox.index('by-entity').getAllKeys([entityType, settings.key])
    await Promise.all(oldOperationIds.map((id) => outbox.delete(id)))
    await outbox.put({
      id: settings.revisionId,
      entityType,
      entityId: settings.key,
      revisionId: settings.revisionId,
      operation: 'upsert',
      createdAt: settings.modifiedAt,
      attemptCount: 0,
      nextAttemptAt: settings.modifiedAt,
      lastErrorCode: null,
    })
  }

  async initializeSettings(
    globalDefaults: GlobalSettings,
    deviceDefaults: DeviceSettings,
  ): Promise<{ global: GlobalSettings; device: DeviceSettings }> {
    const database = await getDatabase()
    const transaction = database.transaction(['settings', 'outbox'], 'readwrite')
    const settings = transaction.objectStore('settings')
    let global = await settings.get('global') as GlobalSettings | undefined
    let device = await settings.get(deviceDefaults.key) as DeviceSettings | undefined
    if (!global) {
      global = globalDefaults
      await settings.put(global)
    } else {
      const storedQuickOptions = (global as Partial<GlobalSettings>).reminderQuickOptions
      const normalizedQuickOptions = normalizeReminderQuickOptions(storedQuickOptions)
      const quickOptionsNeedMigration = (
        !storedQuickOptions
        || storedQuickOptions.firstMinutes !== normalizedQuickOptions.firstMinutes
        || storedQuickOptions.secondMinutes !== normalizedQuickOptions.secondMinutes
        || storedQuickOptions.tomorrowHour !== normalizedQuickOptions.tomorrowHour
      )
      const storedWeekStartsOn = (global as Partial<GlobalSettings>).weekStartsOn
      const weekStartNeedsMigration = !isWeekStartsOn(storedWeekStartsOn)
      if (quickOptionsNeedMigration || weekStartNeedsMigration) {
        if (quickOptionsNeedMigration) {
          global = updateReminderQuickOptions(
            global,
            normalizedQuickOptions,
            deviceDefaults.deviceId,
          )
        }
        if (weekStartNeedsMigration) {
          global = updateWeekStartsOn(
            global,
            normalizeWeekStartsOn(storedWeekStartsOn),
            deviceDefaults.deviceId,
          )
        }
        await settings.put(global)
        await this.replaceSettingsOutboxOperation(transaction, global)
      }
    }
    if (!device) {
      device = deviceDefaults
      await settings.put(device)
      await this.replaceSettingsOutboxOperation(transaction, device)
    } else if (
      device.startModePreferenceVersion !== 2
      || !isAppStartMode(device.startMode)
      || !isUiSize(device.uiSize)
      || typeof device.performanceDiagnosticsEnabled !== 'boolean'
    ) {
      const hasCurrentStartModePreference = device.startModePreferenceVersion === 2
        && isAppStartMode(device.startMode)
      device = updateDeviceSettings(
        {
          ...device,
          startMode: hasCurrentStartModePreference ? device.startMode : 'browse',
          startModePreferenceVersion: 2,
          uiSize: isUiSize(device.uiSize) ? device.uiSize : deviceDefaults.uiSize,
          performanceDiagnosticsEnabled: device.performanceDiagnosticsEnabled === true,
        },
        {
          startMode: hasCurrentStartModePreference ? device.startMode : 'browse',
          startModePreferenceVersion: 2,
          uiSize: isUiSize(device.uiSize) ? device.uiSize : deviceDefaults.uiSize,
          performanceDiagnosticsEnabled: device.performanceDiagnosticsEnabled === true,
        },
        deviceDefaults.deviceId,
      )
      await settings.put(device)
      await this.replaceSettingsOutboxOperation(transaction, device)
    }
    await transaction.done
    return { global, device }
  }

  async getGlobalSettings(): Promise<GlobalSettings | undefined> {
    const global = await (await getDatabase()).get('settings', 'global') as GlobalSettings | undefined
    return global
      ? {
          ...global,
          reminderQuickOptions: normalizeReminderQuickOptions(global.reminderQuickOptions),
          weekStartsOn: normalizeWeekStartsOn(global.weekStartsOn),
          measurementSystem: normalizeMeasurementPreference(global.measurementSystem),
        }
      : undefined
  }

  async getDeviceSettings(deviceId: string): Promise<DeviceSettings | undefined> {
    return (await getDatabase()).get('settings', `device:${deviceId}`) as Promise<DeviceSettings | undefined>
  }

  async getSetting(key: string): Promise<SettingsRecord | undefined> {
    return (await getDatabase()).get('settings', key)
  }

  async commitSettings(settings: SettingsRecord): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['settings', 'outbox'], 'readwrite')
    await transaction.objectStore('settings').put(settings)
    await this.replaceSettingsOutboxOperation(transaction, settings)
    await transaction.done
  }

  async applyRemoteSettings(remote: SettingsRecord): Promise<SettingsRecord> {
    const database = await getDatabase()
    const transaction = database.transaction('settings', 'readwrite')
    const current = await transaction.store.get(remote.key)
    const winner = current && compareSettingsVersions(current, remote) > 0 ? current : remote
    if (!current || winner.revisionId !== current.revisionId || current.localSyncState !== 'synced') {
      await transaction.store.put({ ...winner, localSyncState: winner === remote ? 'synced' : winner.localSyncState })
    }
    await transaction.done
    return winner
  }

  async markSettingsOperationSynced(operationId: string, key: string, revisionId = operationId): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['settings', 'outbox'], 'readwrite')
    const settings = await transaction.objectStore('settings').get(key)
    if (settings?.revisionId === revisionId) {
      await transaction.objectStore('settings').put({ ...settings, localSyncState: 'synced' })
    }
    await transaction.objectStore('outbox').delete(operationId)
    await transaction.done
  }

  async markSettingsOperationFailed(
    operationId: string,
    key: string,
    errorCode: string,
    now = Date.now(),
  ): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['settings', 'outbox'], 'readwrite')
    const outbox = transaction.objectStore('outbox')
    const operation = await outbox.get(operationId)
    if (operation) {
      const attemptCount = operation.attemptCount + 1
      const retryDelay = Math.min(
        APP_SETTINGS.sync.retryMaxMs,
        APP_SETTINGS.sync.retryBaseMs
          * 2 ** Math.min(attemptCount - 1, APP_SETTINGS.sync.retryExponentCap),
      )
      await outbox.put({
        ...operation,
        attemptCount,
        nextAttemptAt: now + retryDelay,
        lastErrorCode: errorCode.slice(0, 120),
      })
    }
    const settings = await transaction.objectStore('settings').get(key)
    if (settings && operation && settings.revisionId === operation.revisionId) {
      await transaction.objectStore('settings').put({ ...settings, localSyncState: 'failed' })
    }
    await transaction.done
  }

  async listNotes(): Promise<Note[]> {
    const database = await getDatabase()
    const notes = (await database.getAll('notes')).map(normalizeStoredNote)
    return notes.sort(
      (left, right) => right.modifiedAt - left.modifiedAt || right.id.localeCompare(left.id),
    )
  }

  async listNotesPage(limit = 50, cursor: NotePageCursor | null = null): Promise<NotePage> {
    const database = await getDatabase()
    const pageSize = Math.max(1, limit)
    const transaction = database.transaction('notes', 'readonly')
    const index = transaction.store.index('by-modified-id')
    const range = cursor
      ? IDBKeyRange.upperBound([cursor.modifiedAt, cursor.id], true)
      : null
    const notes: Note[] = []
    let noteCursor = await index.openCursor(range, 'prev')
    while (noteCursor && notes.length <= pageSize) {
      notes.push(normalizeStoredNote(noteCursor.value))
      noteCursor = await noteCursor.continue()
    }
    await transaction.done
    const hasMore = notes.length > pageSize
    const pageNotes = hasMore ? notes.slice(0, pageSize) : notes
    const last = pageNotes.at(-1)
    return {
      notes: pageNotes,
      nextCursor: hasMore && last ? { modifiedAt: last.modifiedAt, id: last.id } : null,
    }
  }

  async getNote(id: string): Promise<Note | undefined> {
    const note = await (await getDatabase()).get('notes', id)
    return note ? normalizeStoredNote(note) : undefined
  }

  async deleteSynchronizedTrashNotes(noteIds: readonly string[]): Promise<string[]> {
    const database = await getDatabase()
    const transaction = database.transaction(['notes', 'revisions', 'outbox'], 'readwrite')
    const notes = transaction.objectStore('notes')
    const revisions = transaction.objectStore('revisions')
    const outbox = transaction.objectStore('outbox')
    const deleted: string[] = []

    for (const noteId of [...new Set(noteIds)]) {
      const [note, operationIds] = await Promise.all([
        notes.get(noteId),
        outbox.index('by-entity').getAllKeys(['note', noteId]),
      ])
      if (
        note?.status !== 'trash'
        || note.localSyncState !== 'synced'
        || (note.assetIds?.length ?? 0) > 0
        || operationIds.length > 0
      ) continue

      const revisionIds = await revisions.index('by-note').getAllKeys(noteId)
      await Promise.all([
        notes.delete(noteId),
        ...revisionIds.map((revisionId) => revisions.delete(revisionId)),
      ])
      deleted.push(noteId)
    }

    await transaction.done
    return deleted.sort()
  }

  async getRevision(id: string): Promise<NoteRevision | undefined> {
    const revision = await (await getDatabase()).get('revisions', id)
    return revision
      ? {
          ...revision,
          snapshot: normalizeStoredNote(revision.snapshot),
          clientSchemaVersion: revision.clientSchemaVersion ?? 1,
        }
      : undefined
  }

  async getActiveDraft(): Promise<ActiveDraft | undefined> {
    const draft = await (await getDatabase()).get('drafts', 'active')
    return draft ? { ...draft, assetIds: [...(draft.assetIds ?? [])] } : undefined
  }

  async saveActiveDraft(draft: ActiveDraft): Promise<void> {
    const database = await getDatabase()
    await database.put('drafts', { ...draft, updatedAt: Date.now() })
  }

  async clearActiveDraft(expectedNoteId?: string): Promise<void> {
    const database = await getDatabase()
    if (!expectedNoteId) {
      await database.delete('drafts', 'active')
      return
    }
    const transaction = database.transaction('drafts', 'readwrite')
    const current = await transaction.store.get('active')
    if (current?.noteId === expectedNoteId) await transaction.store.delete('active')
    await transaction.done
  }

  async commitLocalNote(
    note: Note,
    reason: NoteRevision['reason'] = 'edit',
  ): Promise<void> {
    await this.commitLocalNotes([{ note, reason }])
  }

  /** Notes, revisions, sync outbox and an optional operation receipt commit atomically. */
  async commitLocalNotes(
    changes: Array<{ note: Note; reason?: NoteRevision['reason']; expectedRevision?: string | null }>,
    receipt?: { key: string; value: string; expected: string | undefined; ownerId?: string },
    checkOnly: Note[] = [],
    copiedAssets: NoteAsset[] = [],
    metaSnapshot?: { prefix: string; entries: Array<{ key: string; value: string | number | boolean }> },
  ): Promise<void> {
    const database = await getDatabase()
    const stores: Array<'notes' | 'revisions' | 'outbox' | 'linkPreviews' | 'meta' | 'assets'> = ['notes', 'revisions', 'outbox', 'linkPreviews']
    if (receipt || metaSnapshot) stores.push('meta')
    if (copiedAssets.length) stores.push('assets')
    const transaction = database.transaction(stores, 'readwrite')
    try {
      if (receipt?.ownerId && (await transaction.objectStore('meta').get('sync-owner'))?.value !== receipt.ownerId) {
        throw new Error('Account changed. Reopen this action in the current account.')
      }
      if (receipt && (await transaction.objectStore('meta').get(receipt.key))?.value !== receipt.expected) {
        throw new Error('This operation changed in another window. Reopen the action and try again.')
      }
      if (metaSnapshot) {
        const current = await transaction.objectStore('meta').getAll(IDBKeyRange.bound(metaSnapshot.prefix, `${metaSnapshot.prefix}\uffff`))
        if (JSON.stringify(current.map(({ key, value }) => ({ key, value }))) !== JSON.stringify(metaSnapshot.entries)) {
          throw new Error('Context connections changed. Reopen the action before deleting notes.')
        }
      }
      for (const note of checkOnly) {
        if ((await transaction.objectStore('notes').get(note.id))?.revisionId !== note.revisionId) {
          throw new Error('A selected note changed. Reopen the action to review the latest notes.')
        }
      }
      for (const { note, reason = 'edit', expectedRevision } of changes) {
        if (expectedRevision !== undefined) {
          const current = await transaction.objectStore('notes').get(note.id)
          if ((current?.revisionId ?? null) !== expectedRevision) {
            throw new Error('A selected note changed. Reopen the action to review the latest notes.')
          }
        }
        const revision: NoteRevision = {
          id: note.revisionId,
          noteId: note.id,
          snapshot: structuredClone(note),
          modifiedAt: note.modifiedAt,
          deviceId: note.lastModifiedDeviceId,
          reason,
          clientSchemaVersion: NOTE_SYNC_SCHEMA_VERSION,
        }
        const operation: SyncOperation = {
          id: note.revisionId,
          entityType: 'note',
          entityId: note.id,
          revisionId: note.revisionId,
          operation: note.status === 'trash' ? 'delete' : 'upsert',
          createdAt: note.modifiedAt,
          attemptCount: 0,
          nextAttemptAt: note.modifiedAt,
          lastErrorCode: null,
        }

        const outbox = transaction.objectStore('outbox')
        const previousOperationIds = await outbox.index('by-entity').getAllKeys(['note', note.id])
        await Promise.all([
          transaction.objectStore('notes').put(note),
          transaction.objectStore('revisions').put(revision),
          ...previousOperationIds.map((operationId) => outbox.delete(operationId)),
          outbox.put(operation),
          putPreviewRecords(transaction.objectStore('linkPreviews'), note.urls),
        ])
      }
      for (const asset of copiedAssets) await transaction.objectStore('assets').add(asset)
      if (receipt) await transaction.objectStore('meta').put({ key: receipt.key, value: receipt.value })
      await transaction.done
    } catch (error) {
      try { transaction.abort() } catch { /* Already aborted by IndexedDB. */ }
      await transaction.done.catch(() => undefined)
      throw error
    }
  }

  async listNoteOperationReceipts(prefix: string): Promise<string[]> {
    const records = await (await getDatabase()).getAll('meta', IDBKeyRange.bound(prefix, `${prefix}\uffff`))
    return records.filter(record => record.key.startsWith(prefix) && typeof record.value === 'string')
      .map(record => record.value as string)
  }

  async updateLinkMetadata(noteId: string, urls: UrlEntity[]): Promise<Note | undefined> {
    const database = await getDatabase()
    const transaction = database.transaction(['notes', 'linkPreviews'], 'readwrite')
    const notesStore = transaction.objectStore('notes')
    const current = await notesStore.get(noteId)
    if (!current) {
      await transaction.done
      return undefined
    }

    const hasAssets = (current.assetIds?.length ?? 0) > 0
    // Preview metadata can reveal what a link is (listing, recipe...), so the
    // automatic type and tags follow it; both are always derived, never authored.
    const classification = classifyContent(current.body, urls, hasAssets)
    const enriched = {
      ...current,
      urls,
      derivedTitle: deriveTitle(current.body, urls, hasAssets),
      contentType: classification.contentType,
      suggestedTags: classification.suggestedTags,
    }
    await Promise.all([
      notesStore.put(enriched),
      putPreviewRecords(transaction.objectStore('linkPreviews'), urls),
    ])
    await transaction.done
    return enriched
  }

  async getCachedLinkMetadata(canonicalUrl: string): Promise<CachedLinkMetadata | undefined> {
    const record = await (await getDatabase()).get('linkPreviews', canonicalUrl)
    if (!record) return undefined
    return {
      fetchedAt: record.fetchedAt,
      metadataPolicyVersion: record.metadataPolicyVersion ?? null,
      metadata: {
        title: record.title,
        description: record.description,
        imageUrl: record.imageUrl,
        durationSeconds: record.durationSeconds,
        siteName: record.siteName,
        author: record.author,
        publishedAt: record.publishedAt,
        ...(record.pageKind ? { pageKind: record.pageKind } : {}),
      },
    }
  }

  async applyRemoteNote(remoteNote: Note): Promise<Note> {
    const hydratedNote = remoteNote as HydratedRemoteNote
    const remotePreviews = Array.isArray(hydratedNote.linkPreviews)
      ? hydratedNote.linkPreviews
          .map(hydratedRemotePreview)
          .filter((preview): preview is LinkPreviewRecord => preview !== null)
      : []
    const { linkPreviews: _ignoredLinkPreviews, ...plainRemoteNote } = hydratedNote
    remoteNote = normalizeStoredNote(plainRemoteNote)
    const database = await getDatabase()
    if (isGoogleKeepRollbackTombstone(remoteNote)) {
      const [current, pendingOperations] = await Promise.all([
        database.get('notes', remoteNote.id),
        database.getAllFromIndex('outbox', 'by-entity', ['note', remoteNote.id]),
      ])
      if (
        (current && compareNoteVersions(current, remoteNote) > 0)
        || pendingOperations.length > 0
      ) return current ?? remoteNote

      const cleanup = database.transaction([
        'notes',
        'revisions',
        'outbox',
        'assets',
      ], 'readwrite')
      const [revisionKeys, outboxKeys, assetKeys] = await Promise.all([
        cleanup.objectStore('revisions').index('by-note').getAllKeys(remoteNote.id),
        cleanup.objectStore('outbox').index('by-entity').getAllKeys(['note', remoteNote.id]),
        cleanup.objectStore('assets').index('by-note').getAllKeys(remoteNote.id),
      ])
      await Promise.all([
        cleanup.objectStore('notes').delete(remoteNote.id),
        ...revisionKeys.map((key) => cleanup.objectStore('revisions').delete(key)),
        ...outboxKeys.map((key) => cleanup.objectStore('outbox').delete(key)),
        ...assetKeys.map((key) => cleanup.objectStore('assets').delete(key)),
      ])
      await cleanup.done
      return remoteNote
    }
    const transaction = database.transaction(['notes', 'revisions', 'linkPreviews'], 'readwrite')
    const notesStore = transaction.objectStore('notes')
    const previewsStore = transaction.objectStore('linkPreviews')
    const previewKeys = [...new Set(
      remoteNote.urls
        .map((url) => url.canonicalUrl)
        .filter((canonicalUrl): canonicalUrl is string => Boolean(canonicalUrl)),
    )]
    const [current, ...cachedPreviews] = await Promise.all([
      notesStore.get(remoteNote.id),
      ...previewKeys.map((canonicalUrl) => previewsStore.get(canonicalUrl)),
    ])
    const currentPreviews = current
      ? current.urls.map(previewRecord).filter((preview): preview is LinkPreviewRecord => preview !== null)
      : []
    const remoteWins = !current || compareNoteVersions(current, remoteNote) <= 0
    const contentWinner = remoteWins ? remoteNote : current
    const winner = normalizeStoredNote({
      ...contentWinner,
      urls: hydrateUrlsFromCachedPreviews(
        contentWinner.urls,
        [...remotePreviews, ...cachedPreviews, ...currentPreviews],
      ),
    })
    const presentationChanged = Boolean(current) && (
      current?.derivedTitle !== winner.derivedTitle
      || JSON.stringify(current?.urls) !== JSON.stringify(winner.urls)
    )

    const writes: Array<Promise<unknown>> = [
      putPreviewRecords(previewsStore, winner.urls),
    ]
    if (remoteWins || presentationChanged) {
      writes.push(notesStore.put({
        ...winner,
        localSyncState: remoteWins ? 'synced' : winner.localSyncState,
      }))
    }
    if (!current || (remoteWins && winner.revisionId !== current.revisionId)) {
      writes.push(
        transaction.objectStore('revisions').put({
          id: remoteNote.revisionId,
          noteId: remoteNote.id,
          snapshot: structuredClone(remoteNote),
          modifiedAt: remoteNote.modifiedAt,
          deviceId: remoteNote.lastModifiedDeviceId,
          reason: 'remote-update',
          clientSchemaVersion: NOTE_SYNC_SCHEMA_VERSION,
        }),
      )
    }
    await Promise.all(writes)

    await transaction.done
    return winner
  }

  async listOutbox(now = Date.now()): Promise<SyncOperation[]> {
    const database = await getDatabase()
    return database.getAllFromIndex('outbox', 'by-next-attempt', IDBKeyRange.upperBound(now))
  }

  async listAllOutbox(): Promise<SyncOperation[]> {
    return (await (await getDatabase()).getAll('outbox'))
      .sort((left, right) => left.createdAt - right.createdAt)
  }

  async getNextOutboxAttemptAt(): Promise<number | null> {
    const database = await getDatabase()
    const cursor = await database.transaction('outbox').store.index('by-next-attempt').openCursor()
    const nextAttemptAt = cursor?.value.nextAttemptAt
    return nextAttemptAt === undefined || nextAttemptAt === Number.MAX_SAFE_INTEGER
      ? null
      : nextAttemptAt
  }

  async getNextAssetAttemptAt(): Promise<number | null> {
    const candidates = await this.listAssetsByStates(['local', 'ready_to_upload', 'error', 'deleted'])
    for (const asset of candidates) {
      if (asset.nextAttemptAt === Number.MAX_SAFE_INTEGER) continue
      if (
        asset.state === 'local'
        || asset.state === 'deleted'
        || (asset.state === 'error' && asset.errorStage !== 'upload')
      ) return asset.nextAttemptAt
      const parent = await this.getNote(asset.noteId)
      if (parent?.localSyncState === 'synced' && parent.assetIds.includes(asset.id)) {
        return asset.nextAttemptAt
      }
    }
    return null
  }

  async listNoteOutbox(noteId: string, now = Date.now()): Promise<SyncOperation[]> {
    const database = await getDatabase()
    const operations = await database.getAllFromIndex('outbox', 'by-entity', ['note', noteId])
    return operations
      .filter((operation) => operation.nextAttemptAt <= now)
      .sort(
        (left, right) => left.createdAt - right.createdAt || left.id.localeCompare(right.id),
      )
  }

  async retryNoteOperation(noteId: string, now = Date.now()): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction('outbox', 'readwrite')
    const outbox = transaction.objectStore('outbox')
    const operations = await outbox.index('by-entity').getAll(['note', noteId])
    await Promise.all(operations.map((operation) => outbox.put({
      ...operation,
      nextAttemptAt: now,
      lastErrorCode: null,
    })))
    await transaction.done
  }

  async retryAccountLimitOperations(now = Date.now()): Promise<number> {
    const database = await getDatabase()
    const transaction = database.transaction('outbox', 'readwrite')
    const outbox = transaction.objectStore('outbox')
    const operations = await outbox.getAll()
    const blocked = operations.filter(
      (operation) => operation.entityType === 'note'
        && operation.lastErrorCode?.startsWith('account-limit:') === true,
    )
    await Promise.all(blocked.map((operation) => outbox.put({
      ...operation,
      attemptCount: 0,
      nextAttemptAt: now,
      lastErrorCode: null,
    })))
    await transaction.done
    return blocked.length
  }

  async markOperationSynced(
    operationId: string,
    noteId: string,
    revisionId = operationId,
  ): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['notes', 'outbox'], 'readwrite')
    const note = await transaction.objectStore('notes').get(noteId)
    if (note?.revisionId === revisionId) {
      await transaction.objectStore('notes').put({ ...note, localSyncState: 'synced' })
    }
    await transaction.objectStore('outbox').delete(operationId)
    await transaction.done
  }

  async markOperationFailed(
    operationId: string,
    noteId: string,
    errorCode: string,
    now = Date.now(),
  ): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['notes', 'outbox'], 'readwrite')
    const outbox = transaction.objectStore('outbox')
    const operation = await outbox.get(operationId)
    if (operation) {
      const attemptCount = operation.attemptCount + 1
      const retryDelay = Math.min(
        APP_SETTINGS.sync.retryMaxMs,
        APP_SETTINGS.sync.retryBaseMs
          * 2 ** Math.min(attemptCount - 1, APP_SETTINGS.sync.retryExponentCap),
      )
      await outbox.put({
        ...operation,
        attemptCount,
        nextAttemptAt: now + retryDelay,
        lastErrorCode: errorCode.slice(0, 120),
      })
    }
    const notes = transaction.objectStore('notes')
    const note = await notes.get(noteId)
    if (note && operation && note.revisionId === operation.revisionId) {
      await notes.put({ ...note, localSyncState: 'failed' })
    }
    await transaction.done
  }

  async markOperationBlockedByAccountLimit(
    operationId: string,
    noteId: string,
    limit: number,
    usage: number,
  ): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['notes', 'outbox'], 'readwrite')
    const outbox = transaction.objectStore('outbox')
    const operation = await outbox.get(operationId)
    if (operation) {
      await outbox.put({
        ...operation,
        attemptCount: operation.attemptCount + 1,
        nextAttemptAt: Number.MAX_SAFE_INTEGER,
        lastErrorCode: `account-limit:${usage}/${limit}`,
      })
    }
    const notes = transaction.objectStore('notes')
    const note = await notes.get(noteId)
    if (note && operation && note.revisionId === operation.revisionId) {
      await notes.put({ ...note, localSyncState: 'failed' })
    }
    await transaction.done
  }

  async discardRejectedNoteRevision(
    operationId: string,
    noteId: string,
    revisionId: string,
  ): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['notes', 'revisions', 'outbox'], 'readwrite')
    const notes = transaction.objectStore('notes')
    const current = await notes.get(noteId)
    if (current?.revisionId === revisionId) await notes.delete(noteId)
    await Promise.all([
      transaction.objectStore('revisions').delete(revisionId),
      transaction.objectStore('outbox').delete(operationId),
    ])
    await transaction.done
  }

  async getMeta(key: string): Promise<string | number | boolean | undefined> {
    return (await getDatabase()).get('meta', key).then((entry) => entry?.value)
  }

  async setMeta(key: string, value: string | number | boolean): Promise<void> {
    await (await getDatabase()).put('meta', { key, value })
  }

  async updateMeta(
    key: string,
    update: (value: string | number | boolean | undefined) => string | number | boolean,
  ): Promise<string | number | boolean> {
    const database = await getDatabase()
    const transaction = database.transaction('meta', 'readwrite')
    const meta = transaction.objectStore('meta')
    const current = await meta.get(key)
    const value = update(current?.value)
    await meta.put({ key, value })
    await transaction.done
    return value
  }

  async deleteMeta(key: string): Promise<void> {
    await (await getDatabase()).delete('meta', key)
  }

  async getRevisions(noteId: string): Promise<NoteRevision[]> {
    const database = await getDatabase()
    const revisions = await database.getAllFromIndex('revisions', 'by-note', noteId)
    return revisions
      .map((revision) => ({
        ...revision,
        snapshot: normalizeStoredNote(revision.snapshot),
        clientSchemaVersion: revision.clientSchemaVersion ?? 1,
      }))
      .sort((left, right) => right.modifiedAt - left.modifiedAt)
  }

  async putAsset(asset: NoteAsset): Promise<void> {
    await (await getDatabase()).put('assets', asset)
  }

  async deleteAsset(assetId: string): Promise<void> {
    await (await getDatabase()).delete('assets', assetId)
  }

  async getAsset(id: string): Promise<NoteAsset | undefined> {
    return (await getDatabase()).get('assets', id)
  }

  async cacheRemoteAssetPreview(
    assetId: string,
    storagePath: string,
    blob: Blob,
  ): Promise<NoteAsset | undefined> {
    const database = await getDatabase()
    const transaction = database.transaction('assets', 'readwrite')
    const current = await transaction.store.get(assetId)
    if (!current || current.state === 'deleted') {
      await transaction.done
      return undefined
    }
    if (assetPreviewBlob(current)) {
      await transaction.done
      return current
    }
    const cached = current.thumbnailStoragePath === storagePath
      ? { ...current, thumbnailBlob: blob }
      : current.masterStoragePath === storagePath
        ? { ...current, processedMasterBlob: blob }
        : current
    if (cached !== current) await transaction.store.put(cached)
    await transaction.done
    return cached
  }

  async listAssets(): Promise<NoteAsset[]> {
    return (await getDatabase()).getAll('assets')
  }

  async listAssetsForNote(noteId: string): Promise<NoteAsset[]> {
    return (await getDatabase()).getAllFromIndex('assets', 'by-note', noteId)
  }

  /**
   * Image attachments that count toward a plan limit: assets that are not deleted and whose note is not in
   * Trash (a draft's images count). Computed locally so the answer holds offline.
   */
  async countLiveImages(): Promise<number> {
    const database = await getDatabase()
    const assets = (await database.getAll('assets')).filter((asset) => asset.state !== 'deleted')
    const noteIds = [...new Set(assets.map((asset) => asset.noteId))]
    const trashed = new Set<string>()
    for (const noteId of noteIds) {
      if ((await database.get('notes', noteId))?.status === 'trash') trashed.add(noteId)
    }
    return assets.filter((asset) => !trashed.has(asset.noteId)).length
  }

  /** The note's own live images, whatever its status (used to size a restore from Trash). */
  async countImagesOfNote(noteId: string): Promise<number> {
    return (await this.listAssetsForNote(noteId)).filter((asset) => asset.state !== 'deleted').length
  }

  async listAssetsByStates(states: NoteAsset['state'][]): Promise<NoteAsset[]> {
    const database = await getDatabase()
    const batches = await Promise.all(
      states.map((state) => database.getAllFromIndex('assets', 'by-state', state)),
    )
    return batches.flat().sort(
      (left, right) => left.nextAttemptAt - right.nextAttemptAt || left.createdAt - right.createdAt,
    )
  }

  async addAssetToDraft(asset: NoteAsset, draft: ActiveDraft): Promise<void> {
    return this.addAssetsToDraft([asset], draft)
  }

  async addAssetsToDraft(assets: NoteAsset[], draft: ActiveDraft): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction(['assets', 'drafts'], 'readwrite')
    await Promise.all([
      ...assets.map((asset) => transaction.objectStore('assets').put(asset)),
      transaction.objectStore('drafts').put({ ...draft, updatedAt: Date.now() }),
    ])
    await transaction.done
  }

  async markAssetDeleted(assetId: string, now = Date.now()): Promise<NoteAsset | undefined> {
    const database = await getDatabase()
    const transaction = database.transaction('assets', 'readwrite')
    const current = await transaction.store.get(assetId)
    if (!current) {
      await transaction.done
      return undefined
    }
    const deleted: NoteAsset = {
      ...current,
      sourceBlob: null,
      processedMasterBlob: null,
      thumbnailBlob: null,
      state: 'deleted',
      errorStage: null,
      attempts: 0,
      nextAttemptAt: now,
      lastError: null,
      modifiedAt: now,
    }
    await transaction.store.put(deleted)
    await transaction.done
    return deleted
  }

  async recoverInterruptedAssetJobs(now = Date.now()): Promise<void> {
    const database = await getDatabase()
    const transaction = database.transaction('assets', 'readwrite')
    const store = transaction.store
    for (const state of ['processing', 'uploading'] as const) {
      let cursor = await store.index('by-state').openCursor(state)
      while (cursor) {
        const asset = cursor.value
        await cursor.update({
          ...asset,
          state: state === 'processing' ? 'local' : 'ready_to_upload',
          errorStage: null,
          nextAttemptAt: now,
          modifiedAt: now,
        })
        cursor = await cursor.continue()
      }
    }
    await transaction.done
  }

  async applyRemoteAsset(remote: RemoteNoteAsset): Promise<NoteAsset> {
    const database = await getDatabase()
    const transaction = database.transaction('assets', 'readwrite')
    const current = await transaction.store.get(remote.id)
    if (current && current.modifiedAt > remote.modifiedAt) {
      await transaction.done
      return current
    }
    const applied: NoteAsset = {
      id: remote.id,
      noteId: remote.noteId,
      kind: remote.kind,
      originalFileName: current?.originalFileName ?? null,
      originalMimeType: remote.originalMimeType,
      masterMimeType: remote.masterMimeType,
      thumbnailMimeType: remote.thumbnailMimeType,
      sourceBlob: current?.sourceBlob ?? null,
      processedMasterBlob: current?.processedMasterBlob ?? null,
      thumbnailBlob: current?.thumbnailBlob ?? null,
      width: remote.width,
      height: remote.height,
      originalSize: remote.originalSize,
      processedSize: remote.processedSize,
      masterStoragePath: remote.masterStoragePath,
      thumbnailStoragePath: remote.thumbnailStoragePath,
      state: remote.state,
      errorStage: null,
      attempts: 0,
      nextAttemptAt: remote.state === 'deleted' ? Number.MAX_SAFE_INTEGER : remote.modifiedAt,
      lastError: null,
      createdAt: remote.createdAt,
      modifiedAt: remote.modifiedAt,
    }
    await transaction.store.put(applied)
    await transaction.done
    return applied
  }
}

export async function requestPersistentStorage(): Promise<boolean | null> {
  if (!navigator.storage?.persist) return null
  try {
    if (await navigator.storage.persisted()) return true
    return navigator.storage.persist()
  } catch {
    return null
  }
}

function readMirroredDeviceId(): string | null {
  try {
    const existing = localStorage.getItem(DEVICE_STORAGE_KEY)
    if (existing) return existing
    const previousKey = `${previousBrandStorageName()}:device:v1`
    const previous = localStorage.getItem(previousKey)
    if (previous) {
      localStorage.setItem(DEVICE_STORAGE_KEY, previous)
      localStorage.removeItem(previousKey)
      return previous
    }
  } catch {
    // A cookie mirror can still recover the stable identity when a browser
    // temporarily withholds localStorage or IndexedDB during startup.
  }
  try {
    const cookie = globalThis.document?.cookie ?? ''
    const prefix = `${DEVICE_ID_COOKIE_NAME}=`
    const value = cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(prefix))
    return value ? decodeURIComponent(value.slice(prefix.length)) || null : null
  } catch {
    // The IndexedDB record remains authoritative when cookies are unavailable.
  }
  return null
}

function mirrorDeviceId(deviceId: string): void {
  try {
    localStorage.setItem(DEVICE_STORAGE_KEY, deviceId)
    localStorage.removeItem(`${previousBrandStorageName()}:device:v1`)
  } catch {
    // IndexedDB remains authoritative when synchronous storage is unavailable.
  }
  try {
    globalThis.document.cookie = [
      `${DEVICE_ID_COOKIE_NAME}=${encodeURIComponent(deviceId)}`,
      'Path=/',
      `Max-Age=${DEVICE_ID_COOKIE_MAX_AGE_SECONDS}`,
      'SameSite=Strict',
      'Secure',
    ].join('; ')
  } catch {
    // IndexedDB and localStorage remain independent durable mirrors.
  }
}

/**
 * Supplies the stable mirrored identity without opening IndexedDB, so the first React
 * route is not held behind WebView database startup. The async reconciliation below
 * remains authoritative if a surviving IndexedDB record disagrees with both mirrors.
 */
export function readOrCreateBootstrapDeviceId(): string {
  const deviceId = readMirroredDeviceId() ?? createId()
  mirrorDeviceId(deviceId)
  return deviceId
}

export async function loadOrCreateDeviceId(): Promise<string> {
  const mirroredDeviceId = readMirroredDeviceId()
  try {
    const database = await getDatabase()
    const transaction = database.transaction('meta', 'readwrite')
    const stored = await transaction.store.get(DEVICE_ID_META_KEY)
    const deviceId = typeof stored?.value === 'string' && stored.value
      ? stored.value
      : mirroredDeviceId ?? createId()
    if (stored?.value !== deviceId) {
      await transaction.store.put({ key: DEVICE_ID_META_KEY, value: deviceId })
    }
    await transaction.done
    mirrorDeviceId(deviceId)
    return deviceId
  } catch {
    const fallback = mirroredDeviceId ?? createId()
    mirrorDeviceId(fallback)
    return fallback
  }
}

export async function resetDatabaseForTests(): Promise<void> {
  if (databasePromise) {
    const database = await databasePromise
    database.close()
  }
  databasePromise = null
  await deleteDB(APP_DATABASE_NAME)
  await deleteDB(previousBrandStorageName())
}
